import React from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'

import { DOC_TYPE_ORDER, TravellerDocDetail, TravellerDocType } from '@/src/types/traveller'

interface TravellerDetailViewProps {
    t: (key: string) => string
    documents: TravellerDocDetail[]
    activeDocType: TravellerDocType | null
    activeDoc: TravellerDocDetail | null
    activeExpiryDateValue: string
    onTabChange: (nextDocType: TravellerDocType) => void
}

const getTabLabel = (t: (key: string) => string, docType: TravellerDocType): string => {
    if (docType === 'PASSPORT') return t('passport')
    if (docType === 'NATIONALID') return t('nationalId')
    if (docType === 'IQAMA') return t('iqamaId')
    return docType
}

const TravellerDetailView: React.FC<TravellerDetailViewProps> = ({
    t,
    documents,
    activeDocType,
    activeDoc,
    activeExpiryDateValue,
    onTabChange
}) => {
    const docNumberLabel =
        activeDocType === 'PASSPORT' ? t('passportNumber') : t('docNumber')

    return (
        <Box sx={{ mt: { xs: 0, md: 3 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: { xs: 700, md: 600 }, fontSize: { xs: '14px', md: '1rem' }, color: 'text.primary' }}>
                {t('travelDocument')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: { xs: 600, md: 500 }, fontSize: { xs: '12px', md: '0.875rem' }, mb: 2 }}>
                {t('travelDocumentSubtitle')}
            </Typography>

            {documents.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('noDocument')}</Typography>
            ) : (
                <>
                    <Tabs
                        value={activeDocType || DOC_TYPE_ORDER[0]}
                        onChange={(_, value) => onTabChange(value as TravellerDocType)}
                        variant="standard"
                        sx={{
                            '& .MuiTabs-flexContainer': { gap: 1 },
                            '& .MuiTabs-indicator': { display: 'none' }
                        }}
                    >
                        {DOC_TYPE_ORDER.map(docType => (
                            <Tab
                                key={docType}
                                value={docType}
                                label={getTabLabel(t, docType)}
                                disableRipple
                                sx={{
                                    minHeight: 32,
                                    textTransform: 'none',
                                    fontWeight: { xs: 400, md: 500 },
                                    fontSize: { xs: '12px', md: '0.875rem' },
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 999,
                                    px: { xs: 1.5, md: 2 },
                                    py: 1,
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        borderColor: 'primary.main'
                                    }
                                }}
                            />
                        ))}
                    </Tabs>

                    <Box
                        sx={{
                            mt: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: { xs: 2.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 500 }}>
                                        {docNumberLabel}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.25 }}>
                                        {activeDoc?.docNumber || '--'}
                                    </Typography>
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 500 }}>
                                        {t('issuingCountry')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.25 }}>
                                        {activeDoc?.countryIssued || '--'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 500 }}>
                                        {t('nationality')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.25 }}>
                                        {activeDoc?.nationality || '--'}
                                    </Typography>
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 500 }}>
                                        {t('expiryDate')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.25 }}>
                                        {activeExpiryDateValue}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
}

export { TravellerDetailView }
export default TravellerDetailView
export type { TravellerDetailViewProps }

